import { Link } from 'react-router-dom';
import { useState } from 'react';

interface BaseCardProps {
  title: string;
  description: string;
  icon?: string;
  href?: string;
  className?: string;
}

export function IconCard({ title, description, icon, className = '' }: BaseCardProps) {
  return (
    <div className={`group p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer ${className}`}>
      {icon && (
        <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
          <i className={`${icon} text-xl`} />
        </div>
      )}
      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed">{description}</p>
    </div>
  );
}

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  categorySlug?: string;
  imageUrl: string;
  featured?: boolean;
}

export function ArticleCard({ slug, title, excerpt, date, category, categorySlug, imageUrl, featured }: ArticleCardProps) {
  const [imgError, setImgError] = useState(false);

  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer">
      <Link to={`/article/${slug}`} className="block">
        <div className={`relative w-full overflow-hidden ${featured ? 'h-60 md:h-72' : 'h-48 md:h-56'}`}>
          {!imgError ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              onError={() => setImgError(true)}
              title={title}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-100 text-foreground-400 text-sm">
              豪门国际
            </div>
          )}
        </div>
      </Link>
      <div className="p-5">
        <div className="flex items-center space-x-3 mb-3">
          {categorySlug ? (
            <Link
              to={`/category/${categorySlug}`}
              className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              {category}
            </Link>
          ) : (
            <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
              {category}
            </span>
          )}
          <span className="text-xs text-foreground-400">{date}</span>
        </div>
        <Link to={`/article/${slug}`} className="block">
          <h3 className={`font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 line-clamp-2 ${featured ? 'text-lg md:text-xl' : 'text-base'}`}>
            {title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{excerpt}</p>
      </div>
    </article>
  );
}

interface StatCardProps {
  value: string;
  suffix: string;
  label: string;
}

export function StatCard({ value, suffix, label }: StatCardProps) {
  return (
    <div className="text-center">
      <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500">
        <span className="inline-block">
          <span>{value}</span>
          <span className="text-xl md:text-2xl ml-1 text-primary-400">{suffix}</span>
        </span>
      </div>
      <p className="mt-2 text-sm text-foreground-600">{label}</p>
    </div>
  );
}

export function SectionTag({ children }: { children: string }) {
  return (
    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4 tracking-wider">
      {children}
    </span>
  );
}

export function SectionTitle({ children, className = '' }: { children: string; className?: string }) {
  return (
    <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 ${className}`}>
      {children}
    </h2>
  );
}

export function SectionDescription({ children }: { children: string }) {
  return (
    <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
      {children}
    </p>
  );
}